/**
 * Pick companies from a list.
 *
 * Two of these sit side by side on the user editor's Companies tab: which
 * companies a user may look at, and which it may export snapshots into. They
 * used to be two walls of wrapping checkboxes — 22 of them each, unsearchable,
 * and identical to look at, so there was no telling one list from the other.
 *
 * The chrome is SelectorBases' (settingsPalette.useChecklistStyles), on purpose:
 * the two selectors appear on the same screen and picking from a list should
 * feel the same wherever you do it. Companies have nothing to group by, so this
 * is the flat version.
 */

import React, { useMemo, useState } from "react";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { SEARCH_INPUT_PROPS, useChecklistStyles } from "./settingsPalette";

export default function SelectorCompanies(props) {
  const classes = useChecklistStyles();
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => new Set((props.selectedCompanyIDs || []).map(String)),
    [props.selectedCompanyIDs]
  );
  const available = props.companies || [];

  const matching = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return available;
    return available.filter((company) => (company.name || "").toLowerCase().includes(needle));
  }, [available, query]);

  const setSelection = (nextSet) => props.onChange(Array.from(nextSet));

  const toggle = (companyID) => {
    const next = new Set(selected);
    if (next.has(String(companyID))) {
      next.delete(String(companyID));
    } else {
      next.add(String(companyID));
    }
    setSelection(next);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.toolbar}>
        <TextField
          className={classes.search}
          size="small"
          placeholder="Search companies"
          InputProps={SEARCH_INPUT_PROPS}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <span className={classes.count}>
          {selected.size} / {available.length}
        </span>
        <Button
          className={classes.linkButton}
          onClick={() => setSelection(new Set(available.map((company) => String(company._id))))}
        >
          All
        </Button>
        <Button className={classes.linkButton} onClick={() => setSelection(new Set())}>
          None
        </Button>
      </div>

      <div className={`${classes.scroller} ${classes.scrollerCapped}`}>
        {matching.length === 0 && (
          <div className={classes.empty}>{props.emptyText || "No companies to show."}</div>
        )}
        {matching.map((company) => (
          <div key={String(company._id)} className={classes.row} onClick={() => toggle(company._id)}>
            <Checkbox
              size="small"
              checked={selected.has(String(company._id))}
              onChange={() => toggle(company._id)}
            />
            <span className={classes.rowLabel}>{company.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
